'use client';

import { useRunnerRequestUiRuntime } from '../runtime';

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EditorContent, useEditor } from '@tiptap/react';
import { type JSONContent, type Range } from '@tiptap/core';
import { useTranslations } from 'next-intl';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import TaskItem from '@tiptap/extension-task-item';
import TaskList from '@tiptap/extension-task-list';
import LinkExtension from '@tiptap/extension-link';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Typography from '@tiptap/extension-typography';
import { common, createLowlight } from 'lowlight';
import { ChevronDown } from 'lucide-react';
import { useTiptapCommands } from '../hooks/useTiptapCommands';
import { TiptapBubbleToolbar } from './TiptapBubbleToolbar';
import { SlashCommandPopup, type SlashCommandPopupRef } from './SlashCommandPopup';
import { EntityReferencePickerModal } from './EntityReferencePickerModal';
import { EntityReference } from '../lib/tiptap/extensions/EntityReference';
import {
  SlashCommand,
  type SlashCommandItem,
  type SlashCommandRenderState,
} from '../lib/tiptap/extensions/SlashCommand';
import { PasteEntityId } from '../lib/tiptap/extensions/PasteEntityId';
import { withEntityIdPrefix } from '../lib/entityId';
import type { EntityResult } from '../runtime';

const lowlight = createLowlight(common);

const defaultContent: JSONContent = {
  type: 'doc',
  content: [{ type: 'paragraph' }],
};

type TiptapEditorProps = {
  value?: JSONContent;
  onChangeAction: (content: JSONContent) => void;
  placeholder?: string;
  allowedEntityTypes?: string[];
  hideToolbar?: boolean;
  compact?: boolean;
  portalContainer?: React.RefObject<HTMLElement | null>;
  variant?: 'convention' | 'general';
};

type ToolbarButtonProps = {
  label: string;
  title: string;
  onClickAction: () => void;
  disabled: boolean;
  isActive?: boolean;
};

const ToolbarButton = ({ label, title, onClickAction, disabled, isActive = false }: ToolbarButtonProps) => {
  return (
    <button
      type="button"
      className={`btn btn-xs ${isActive ? 'btn-primary' : 'btn-ghost'}`}
      title={title}
      aria-label={label}
      disabled={disabled}
      onClick={onClickAction}
    >
      {label}
    </button>
  );
};

type IconToolbarButtonProps = {
  label: string;
  title: string;
  onClickAction: () => void;
  disabled: boolean;
  isActive?: boolean;
  icon: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>;
};

const IconToolbarButton = ({
  label,
  title,
  onClickAction,
  disabled,
  isActive = false,
  icon: Icon,
}: IconToolbarButtonProps) => {
  return (
    <button
      type="button"
      className={`btn btn-xs ${isActive ? 'btn-primary' : 'btn-ghost'} px-2`}
      title={title}
      aria-label={label}
      disabled={disabled}
      onClick={onClickAction}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
};

export const TiptapEditor = ({
  value,
  onChangeAction,
  placeholder,
  allowedEntityTypes,
  hideToolbar = false,
  compact = false,
  portalContainer,
  variant = 'convention',
}: TiptapEditorProps) => {
  const t = useTranslations('components.tiptapEditor');
  const { useWorkspaceScope, prefetchAvailableProviders, fetchEntityTitle } = useRunnerRequestUiRuntime();
  const { token, projectId } = useWorkspaceScope();
  const effectivePlaceholder = placeholder ?? t('placeholder');
  const normalizedValue = value ?? defaultContent;
  const slashCommandPopupRef = useRef<SlashCommandPopupRef>(null);
  const [slashCommandState, setSlashCommandState] = useState<SlashCommandRenderState | null>(null);
  const [pickerState, setPickerState] = useState<{ command: SlashCommandItem; range: Range } | null>(null);

  const closeSlashCommand = () => {
    setSlashCommandState(null);
  };

  // Slash 메뉴는 타입만 고르고, 실제 엔티티 검색/선택은 picker dialog로 넘긴다.
  // suggestion range를 보존해 두었다가 선택 시 그 자리에 entityReference를 삽입한다.
  const handleSelectCommand = (command: SlashCommandItem) => {
    if (!slashCommandState) return;
    setPickerState({ command, range: slashCommandState.range });
    closeSlashCommand();
  };

  const handlePickerClose = () => {
    setPickerState(null);
    editor?.commands.focus();
  };

  const handlePickerSelect = (entity: EntityResult) => {
    if (!editor || !pickerState) return;
    editor
      .chain()
      .focus()
      .deleteRange(pickerState.range)
      .insertContent({
        type: 'entityReference',
        attrs: {
          entityType: entity.entityType,
          entityId: withEntityIdPrefix(entity.entityType, entity.id),
          label: entity.insertLabel ?? entity.title,
          ...(entity.path ? { path: entity.path } : {}),
          ...(entity.htmlUrl ? { htmlUrl: entity.htmlUrl } : {}),
        },
      })
      .insertContent({ type: 'text', text: ' ' })
      .run();
    setPickerState(null);
  };

  useEffect(() => {
    if (token && projectId) {
      prefetchAvailableProviders(token, projectId);
    }
  }, [token, projectId]);

  const extensions = useMemo(
    () => [
      StarterKit.configure({ codeBlock: false, link: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      TaskList,
      TaskItem.configure({ nested: true }),
      LinkExtension.configure({ openOnClick: false, autolink: false, defaultProtocol: 'https' }),
      CodeBlockLowlight.configure({ lowlight }),
      Placeholder.configure({ placeholder: effectivePlaceholder }),
      TextStyle,
      Color,
      Typography,
      EntityReference,
      PasteEntityId.configure({ token, projectId, fetchEntityTitle }),
      allowedEntityTypes
        ? SlashCommand.configure({
            allowedCommands: allowedEntityTypes,
            onOpen: setSlashCommandState,
            onUpdate: setSlashCommandState,
            onClose: closeSlashCommand,
            onKeyDown: (event) => slashCommandPopupRef.current?.onKeyDown(event) ?? false,
          })
        : SlashCommand.configure({
            onOpen: setSlashCommandState,
            onUpdate: setSlashCommandState,
            onClose: closeSlashCommand,
            onKeyDown: (event) => slashCommandPopupRef.current?.onKeyDown(event) ?? false,
          }),
    ],
    [allowedEntityTypes, effectivePlaceholder, token, projectId, fetchEntityTitle],
  );

  const [, forceUpdate] = useState(0);
  const isInitialSync = useRef(true);

  const editor = useEditor(
    {
      immediatelyRender: false,
      extensions,
      content: normalizedValue,
      onUpdate: ({ editor: currentEditor }) => {
        onChangeAction(currentEditor.getJSON());
      },
      onSelectionUpdate: () => {
        forceUpdate((c) => c + 1);
      },
    },
    // useEditor는 옵션이 바뀌어도 편집기를 재생성하지 않는다. placeholder는 확장
    // 설정에 한 번 고정되므로, prop이 바뀌면(정책 선택 등) 편집기를 다시 만들어야
    // 새 placeholder가 반영된다. 재생성 시 content는 현재 값으로 다시 시드된다.
    [effectivePlaceholder],
  );

  const commands = useTiptapCommands(editor, variant);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return;
    }

    if (isInitialSync.current) {
      isInitialSync.current = false;
      return;
    }

    const currentContent = JSON.stringify(editor.getJSON());
    const nextContent = JSON.stringify(normalizedValue);

    if (currentContent === nextContent) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled && !editor.isDestroyed) editor.commands.setContent(normalizedValue);
    });
    return () => {
      cancelled = true;
    };
  }, [editor, normalizedValue]);

  return (
    <div
      className={`flex h-full flex-col [&_.ProseMirror]:!border-0 [&_.ProseMirror]:!rounded-none [&_.ProseMirror]:!outline-none ${compact ? 'tiptap-compact-wrapper' : ''}`}
    >
      {!hideToolbar && (
        <div className="flex flex-col border-b border-base-200">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2 px-3 py-2">
            <div className="dropdown dropdown-bottom dropdown-start">
              <button
                type="button"
                tabIndex={0}
                className="btn btn-xs btn-ghost gap-1"
                disabled={commands.heading.disabled}
                aria-label="Text style"
              >
                <span className="text-xs">{commands.heading.currentLabel}</span>
                <ChevronDown className="h-3 w-3" aria-hidden="true" />
              </button>
              <ul
                tabIndex={0}
                className="dropdown-content menu z-10 mt-1 w-40 rounded-md bg-base-100 p-1 shadow ring-1 ring-base-300"
              >
                {commands.heading.options.map((option) => (
                  <li key={option.level}>
                    <button
                      type="button"
                      className={option.isActive ? 'active text-xs' : 'text-xs'}
                      onClick={option.onClickAction}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="h-5 w-px bg-base-300" />

            {commands.inline.items.map((item) => (
              <IconToolbarButton key={item.label} {...item} />
            ))}

            <div className="h-5 w-px bg-base-300" />

            {commands.list.items.map((item) => (
              <IconToolbarButton key={item.label} {...item} />
            ))}

            <div className="h-5 w-px bg-base-300" />

            {commands.block.items.map((item) => (
              <IconToolbarButton key={item.label} {...item} />
            ))}

            <div className="h-5 w-px bg-base-300" />

            <IconToolbarButton {...commands.link} />
          </div>

          {commands.variant === 'convention' && (
            <div className="flex flex-wrap items-center gap-1 border-t border-base-200 px-3 py-1.5">
              {commands.markers.items.map((item) => (
                <ToolbarButton
                  key={item.label}
                  label={item.label}
                  title={item.title}
                  disabled={item.disabled}
                  isActive={item.isActive}
                  onClickAction={item.onClickAction}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="relative min-h-0 flex-1 overflow-visible">
        <div className="min-h-0 h-full overflow-y-auto">
          <EditorContent
            editor={editor}
            className={`tiptap !rounded-none !border-0 !p-0 [&_.ProseMirror]:!m-0 [&_.ProseMirror]:!py-3 [&_.ProseMirror]:!px-4 ${compact ? '' : '[&_.ProseMirror]:!min-h-[240px]'}`}
          />
        </div>

        {editor && slashCommandState ? (
          <SlashCommandPopup
            ref={slashCommandPopupRef}
            editor={editor}
            range={slashCommandState.range}
            query={slashCommandState.query}
            items={slashCommandState.items}
            clientRect={slashCommandState.clientRect}
            token={token}
            projectId={projectId}
            onCloseAction={closeSlashCommand}
            onSelectCommandAction={handleSelectCommand}
            portalContainer={portalContainer}
          />
        ) : null}

        {editor && pickerState && typeof document !== 'undefined'
          ? createPortal(
              <EntityReferencePickerModal
                isOpen
                command={pickerState.command}
                token={token}
                projectId={projectId}
                onCloseAction={handlePickerClose}
                onSelectAction={handlePickerSelect}
              />,
              document.body,
            )
          : null}

        {editor ? <TiptapBubbleToolbar editor={editor} /> : null}
      </div>
    </div>
  );
};
